import { useState, useEffect, useCallback } from "react";
import { CalendarDays, TrendingUp, Clock, BarChart3 } from "lucide-react";

import { API_BASE as API } from '../config';

// Defensive frontend mirror of backend ARCHIVED_BOT_IDS. If the backend
// filter ever misses a row, we still refuse to pick a dead bot as the
// dashboard default selection (e.g. doge-beast-15m's crash day).
const ARCHIVED_BOT_IDS = new Set([
  // Mirrors backend ARCHIVED_BOT_IDS (eu_bot_router.py:4553).
  // Keep in sync on every retirement. Backend is source of truth.
  "doge-beast-15m",  // killed after -$507 crash day
  "multi-dca",        // legacy multi-asset runner
  "dca-master",       // legacy single-bot namespace
  "dca-skl",          // Commit 73: migrated to dca-enj (ENJ cc=4.0 75% better)
  "dca-near",         // Commit 51 consolidation
  "dca-aave",         // Commit 51 consolidation
  "dca-render",       // superseded by dca-fleet RENDER
  "dca-sol",          // Commit 61: 2yr backtest negative edge
  "dca-axs",          // Commit 61: 42% DD
  "dca-eth",          // Commit 61: 47% DD
]);

export default function PerformanceTab({ token }) {
  const [equityData, setEquityData] = useState([]);
  const [fleetStats, setFleetStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBotId, setSelectedBotId] = useState(null);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [r1, r2] = await Promise.allSettled([
        fetch(`${API}/api/eu-bot/dca/equity-history`, { headers }),
        fetch(`${API}/api/eu-bot/dca/fleet-stats`, { headers }),
      ]);
      if (r1.status === "fulfilled" && r1.value.ok) {
        const data = await r1.value.json();
        setEquityData(data.history || []);
        if (!selectedBotId && (data.history || []).length > 0) {
          // Pick the first LIVE bot id in the history stream — never
          // default to an archived bot even if one leaks through.
          const firstLive = (data.history || []).find(
            (h) => h.bot_id && !ARCHIVED_BOT_IDS.has(h.bot_id)
          );
          if (firstLive) setSelectedBotId(firstLive.bot_id);
        }
      }
      if (r2.status === "fulfilled" && r2.value.ok) {
        setFleetStats(await r2.value.json());
      }
    } catch {}
    setLoading(false);
  }, [token, selectedBotId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="text-center py-20 text-zinc-600 font-mono text-sm">Loading performance data...</div>;
  if (!equityData.length) return <div className="text-center py-20 text-zinc-600 font-mono text-sm">No performance data yet</div>;

  // Group data by bot
  const botGroups = {};
  for (const d of equityData) {
    const bid = d.bot_id || "unknown";
    if (!botGroups[bid]) botGroups[bid] = [];
    botGroups[bid].push(d);
  }

  // Get available LIVE bots and find best one. Archived bots are never
  // offered in the dropdown or picked as default — they're historical.
  const liveBotEntries = Object.entries(botGroups).filter(([bid]) => !ARCHIVED_BOT_IDS.has(bid));
  const botIds = liveBotEntries.map(([bid]) => bid);
  let bestBotId = null;
  let bestPnl = -Infinity;
  for (const [bid, entries] of liveBotEntries) {
    const lastPnl = entries[entries.length - 1]?.pnl || 0;
    if (lastPnl > bestPnl) {
      bestPnl = lastPnl;
      bestBotId = bid;
    }
  }

  // If selectedBotId ever ends up pointing at an archived bot (e.g. stale
  // state from a previous session), override it to the best live bot.
  const effectiveSelected =
    selectedBotId && !ARCHIVED_BOT_IDS.has(selectedBotId) ? selectedBotId : null;
  const activeBotId = effectiveSelected || bestBotId;
  const filteredData = activeBotId ? botGroups[activeBotId] : equityData;

  // Group data by date
  const byDate = {};
  let prevEquity = null;
  for (const d of filteredData) {
    const date = new Date(d.t);
    const key = date.toISOString().split("T")[0];
    if (!byDate[key]) {
      byDate[key] = { date: key, entries: [], firstEquity: d.equity, lastEquity: d.equity, pnl: d.pnl, deals: d.deals, minEquity: d.equity, maxEquity: d.equity };
    }
    byDate[key].entries.push(d);
    byDate[key].lastEquity = d.equity;
    byDate[key].pnl = d.pnl;
    byDate[key].deals = d.deals;
    byDate[key].minEquity = Math.min(byDate[key].minEquity, d.equity);
    byDate[key].maxEquity = Math.max(byDate[key].maxEquity, d.equity);
  }

  const days = Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));

  // Calculate daily PnL changes
  for (let i = 0; i < days.length; i++) {
    if (i === 0) {
      days[i].dayPnl = days[i].lastEquity - days[i].firstEquity;
    } else {
      days[i].dayPnl = days[i].lastEquity - days[i - 1].lastEquity;
    }
    days[i].dayPnlPct = days[i].firstEquity > 0 ? (days[i].dayPnl / days[i].firstEquity * 100) : 0;
  }

  // Group by hour for heatmap
  const byHour = {};
  let prevEq = null;
  for (const d of filteredData) {
    const hour = new Date(d.t).getHours();
    if (!byHour[hour]) byHour[hour] = { total: 0, count: 0 };
    if (prevEq !== null) {
      byHour[hour].total += d.equity - prevEq;
      byHour[hour].count += 1;
    }
    prevEq = d.equity;
  }

  // Summary stats
  const totalPnl = days.reduce((sum, d) => sum + d.dayPnl, 0);
  const winDays = days.filter(d => d.dayPnl > 0).length;
  const lossDays = days.filter(d => d.dayPnl < 0).length;
  const bestDay = days.reduce((best, d) => d.dayPnl > best.dayPnl ? d : best, days[0]);
  const worstDay = days.reduce((worst, d) => d.dayPnl < worst.dayPnl ? d : worst, days[0]);

  const probation = fleetStats?.probation;
  const ghostsHidden = fleetStats?.fleet?.ghost_ids_hidden || [];
  const shadowBots = fleetStats?.shadow_bots || [];

  return (
    <div className="space-y-4" data-testid="performance-tab">

      {/* ── AAVE Probation Banner ── */}
      {probation?.active && (
        <div
          data-testid="probation-banner"
          className={`rounded-xl border px-4 py-3 flex items-center justify-between text-xs font-mono ${
            probation.should_pause
              ? "bg-red-950/40 border-red-700/50 text-red-300"
              : "bg-amber-950/30 border-amber-700/40 text-amber-300"
          }`}
        >
          <div>
            <span className="font-bold uppercase tracking-wider mr-2">
              {probation.should_pause ? "PROBATION → PAUSE" : "PROBATION WATCH"}
            </span>
            <span className="text-zinc-400">
              {probation.bot_id}: {probation.deals_since}/{probation.min_deals} deals,
              avg {probation.avg_profit_pct?.toFixed(2)}% (need ≥{probation.min_avg_profit_pct}%)
            </span>
          </div>
          {probation.should_pause && (
            <span className="text-red-400 font-bold">Bot will receive pause command on next heartbeat.</span>
          )}
        </div>
      )}

      {/* ── Ghost-bot filter notice ── */}
      {ghostsHidden.length > 0 && (
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2 text-[11px] font-mono text-zinc-500">
          Hiding {ghostsHidden.length} stale bot{ghostsHidden.length === 1 ? "" : "s"}: {ghostsHidden.join(", ")}.
          <span className="ml-2 text-zinc-600">(no heartbeat or deal in 48h)</span>
        </div>
      )}

      {/* ── Fleet APY Comparison ── */}
      {fleetStats && fleetStats.bots?.length > 0 && (
        <div className="bg-zinc-900/60 border border-zinc-800/60 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-purple-400" />
            <span className="text-sm font-mono font-bold text-purple-400">Fleet APY Comparison</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-zinc-800/60">
            {fleetStats.bots.filter(b => b.connected).map(b => {
              const hasData = b.deals > 0 && b.actual_apy > 0;
              const vs = b.actual_apy - b.backtest_apy;
              return (
                <div key={b.bot_id} className="px-4 py-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-mono font-bold text-zinc-200">{b.base}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono border ${b.connected ? "text-emerald-400 border-emerald-500/20 bg-emerald-500/5" : "text-zinc-600 border-zinc-700"}`}>
                      {b.connected ? "LIVE" : "OFFLINE"}
                    </span>
                  </div>
                  <div className="flex items-end gap-3">
                    <div>
                      <div className="text-[9px] text-zinc-500 uppercase mb-0.5">Actual</div>
                      <div className={`text-xl font-black font-mono ${hasData ? (b.actual_apy > b.backtest_apy ? "text-emerald-400" : "text-amber-400") : "text-zinc-600"}`}>
                        {hasData ? `${b.actual_apy.toFixed(1)}%` : "—"}
                      </div>
                    </div>
                    <div className="text-zinc-700 mb-1">vs</div>
                    <div>
                      <div className="text-[9px] text-zinc-500 uppercase mb-0.5">Backtest</div>
                      <div className="text-xl font-black font-mono text-blue-400">{b.backtest_apy}%</div>
                    </div>
                    {hasData && (
                      <div className="ml-auto text-right">
                        <div className="text-[9px] text-zinc-500 uppercase mb-0.5">Delta</div>
                        <div className={`text-sm font-bold font-mono ${vs >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                          {vs >= 0 ? "+" : ""}{vs.toFixed(1)}%
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="mt-2 text-[10px] text-zinc-600 font-mono">
                    {b.deals} deals · ${(b.realized_pnl ?? b.total_profit ?? 0).toFixed(2)} realized · net ${(b.net_pnl ?? 0).toFixed(2)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Shadow Fleet (paper-trading validators) ── */}
      {shadowBots.length > 0 && (
        <div
          data-testid="shadow-fleet"
          className="bg-zinc-900/60 border border-indigo-700/40 rounded-xl overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-indigo-700/30 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-indigo-300" />
            <span className="text-sm font-mono font-bold text-indigo-300">
              Shadow Fleet
            </span>
            <span className="text-[10px] px-1.5 py-0.5 rounded font-mono border border-indigo-500/30 bg-indigo-500/10 text-indigo-300 ml-1">
              SHADOW
            </span>
            <span className="text-[10px] text-zinc-500 ml-auto">
              paper trading · excluded from fleet totals
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-zinc-800/60">
            {shadowBots.map(b => {
              const hasData = b.deals > 0 && b.actual_apy > 0;
              const vs = b.actual_apy - b.backtest_apy;
              return (
                <div key={b.bot_id} className="px-4 py-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-mono font-bold text-zinc-200">{b.base}</span>
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] px-1.5 py-0.5 rounded font-mono border border-indigo-500/30 bg-indigo-500/10 text-indigo-300">
                        SHADOW
                      </span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono border ${b.connected ? "text-emerald-400 border-emerald-500/20 bg-emerald-500/5" : "text-zinc-600 border-zinc-700"}`}>
                        {b.connected ? "LIVE" : "OFFLINE"}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-end gap-3">
                    <div>
                      <div className="text-[9px] text-zinc-500 uppercase mb-0.5">Paper APY</div>
                      <div className={`text-xl font-black font-mono ${hasData ? (b.actual_apy > b.backtest_apy ? "text-emerald-400" : "text-amber-400") : "text-zinc-600"}`}>
                        {hasData ? `${b.actual_apy.toFixed(1)}%` : "—"}
                      </div>
                    </div>
                    <div className="text-zinc-700 mb-1">vs</div>
                    <div>
                      <div className="text-[9px] text-zinc-500 uppercase mb-0.5">Backtest</div>
                      <div className="text-xl font-black font-mono text-blue-400">{b.backtest_apy}%</div>
                    </div>
                    {hasData && (
                      <div className="ml-auto text-right">
                        <div className="text-[9px] text-zinc-500 uppercase mb-0.5">Delta</div>
                        <div className={`text-sm font-bold font-mono ${vs >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                          {vs >= 0 ? "+" : ""}{vs.toFixed(1)}%
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="mt-2 text-[10px] text-zinc-600 font-mono">
                    {b.deals} deals · realized ${(b.realized_pnl ?? b.total_profit ?? 0).toFixed(2)} · net ${(b.net_pnl ?? 0).toFixed(2)} · cap ${b.capital}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bot Selector */}
      {botIds.length > 1 && (
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-4">
          <div className="text-[10px] text-zinc-500 uppercase font-mono mb-2">Select Bot</div>
          <div className="flex gap-2 flex-wrap">
            {botIds.map(bid => (
              <button
                key={bid}
                onClick={() => setSelectedBotId(bid)}
                data-testid={`bot-selector-${bid}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-mono border transition-all ${
                  activeBotId === bid
                    ? "bg-teal-600/20 border-teal-600/40 text-teal-400"
                    : "border-zinc-700 text-zinc-500 hover:text-zinc-300"
                }`}
              >
                {bid}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3">
          <div className="text-[10px] text-zinc-500 uppercase font-mono mb-1">Total PnL</div>
          <div className={`text-xl font-mono font-bold ${totalPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid="perf-total-pnl">
            {totalPnl >= 0 ? '+' : ''}${totalPnl.toFixed(2)}
          </div>
        </div>
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3">
          <div className="text-[10px] text-zinc-500 uppercase font-mono mb-1">Days Tracked</div>
          <div className="text-xl font-mono font-bold text-white">{days.length}</div>
        </div>
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3">
          <div className="text-[10px] text-zinc-500 uppercase font-mono mb-1">Win/Loss Days</div>
          <div className="text-xl font-mono font-bold">
            <span className="text-emerald-400">{winDays}</span>
            <span className="text-zinc-600">/</span>
            <span className="text-red-400">{lossDays}</span>
          </div>
        </div>
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3">
          <div className="text-[10px] text-zinc-500 uppercase font-mono mb-1">Best Day</div>
          <div className="text-lg font-mono font-bold text-emerald-400">+${bestDay.dayPnl.toFixed(2)}</div>
          <div className="text-[9px] text-zinc-600 font-mono">{bestDay.date}</div>
        </div>
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3">
          <div className="text-[10px] text-zinc-500 uppercase font-mono mb-1">Worst Day</div>
          <div className="text-lg font-mono font-bold text-red-400">${worstDay.dayPnl.toFixed(2)}</div>
          <div className="text-[9px] text-zinc-600 font-mono">{worstDay.date}</div>
        </div>
      </div>

      {/* Daily Performance Calendar */}
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-5">
        <div className="flex items-center gap-2 mb-4">
          <CalendarDays className="w-5 h-5 text-teal-400" />
          <span className="text-base font-mono font-bold text-teal-400">Daily Performance</span>
        </div>
        <div className="space-y-2" data-testid="daily-calendar">
          {days.map(day => {
            const pnlPct = day.dayPnlPct;
            const barWidth = Math.min(Math.abs(pnlPct) * 20, 100);
            const isPositive = day.dayPnl >= 0;
            return (
              <div key={day.date} className="flex items-center gap-3 py-2 border-b border-zinc-800/50 last:border-0">
                <div className="w-24 text-xs font-mono text-zinc-500">{new Date(day.date + 'T12:00:00').toLocaleDateString("el-GR", { weekday: "short", day: "2-digit", month: "2-digit" })}</div>
                <div className="flex-1 flex items-center gap-2">
                  <div className="flex-1 bg-zinc-800/50 rounded-full h-5 relative overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-500 ${isPositive ? 'bg-emerald-500/30' : 'bg-red-500/30'}`}
                      style={{ width: `${barWidth}%` }} />
                    <span className={`absolute inset-0 flex items-center justify-center text-[10px] font-mono font-bold ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                      {isPositive ? '+' : ''}${day.dayPnl.toFixed(2)} ({pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(2)}%)
                    </span>
                  </div>
                </div>
                <div className="w-16 text-right text-[10px] font-mono text-zinc-600">{day.entries.length} pts</div>
                <div className="w-16 text-right text-[10px] font-mono text-zinc-500">{day.deals || 0} deals</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Hour Heatmap */}
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-5">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-amber-400" />
          <span className="text-base font-mono font-bold text-amber-400">Hourly Heatmap</span>
          <span className="text-[10px] text-zinc-600 font-mono">(equity change per hour)</span>
        </div>
        <div className="grid grid-cols-8 md:grid-cols-12 gap-1.5" data-testid="hourly-heatmap">
          {Array.from({ length: 24 }, (_, h) => {
            const data = byHour[h];
            const avg = data && data.count > 0 ? data.total / data.count : 0;
            const total = data ? data.total : 0;
            const intensity = Math.min(Math.abs(total) / 5, 1);
            const isPos = total >= 0;
            return (
              <div key={h} className="relative group" title={`${h}:00 — $${total.toFixed(2)} total, ${data?.count || 0} samples`}>
                <div className={`rounded-md p-2 text-center border transition-all ${
                  !data ? 'border-zinc-800/50 bg-zinc-900/30' :
                  isPos ? `border-emerald-800/40 bg-emerald-500/${Math.max(Math.round(intensity * 30), 5)}` :
                  `border-red-800/40 bg-red-500/${Math.max(Math.round(intensity * 30), 5)}`
                }`} style={{
                  backgroundColor: !data ? 'rgba(24,24,27,0.3)' :
                    isPos ? `rgba(16,185,129,${intensity * 0.3})` :
                    `rgba(239,68,68,${intensity * 0.3})`
                }}>
                  <div className="text-[10px] text-zinc-500 font-mono">{h.toString().padStart(2, '0')}:00</div>
                  <div className={`text-xs font-mono font-bold ${!data ? 'text-zinc-700' : isPos ? 'text-emerald-400' : 'text-red-400'}`}>
                    {data ? `${total >= 0 ? '+' : ''}$${total.toFixed(1)}` : '—'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-center gap-4 mt-3 text-[9px] font-mono text-zinc-600">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500/30" /> Loss</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-zinc-800" /> No data</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500/30" /> Profit</span>
        </div>
      </div>
    </div>
  );
}
